import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import {
  ArrowLeft, Upload, CheckCircle, AlertCircle, Download,
  FileText, ArrowRight, Settings, X, Check, HelpCircle, Cloud, Database,
  ChevronDown, Loader2
} from 'lucide-react';
import {
  useBatchImportPromotersMutation,
  useGetHubSpotStatusQuery,
  useGetHubSpotAuthUrlQuery,
  useLazyGetHubSpotContactsQuery,
  useGetHubSpotContactPropertiesQuery,
  useDisconnectHubSpotMutation,
  useGetSalesforceStatusQuery,
  useGetSalesforceAuthUrlQuery,
  useLazyGetSalesforceContactsQuery,
  useDisconnectSalesforceMutation,
  type BatchImportResponse,
  type BatchImportPromoterEntry,
  type BatchImportResult,
  type HubSpotContact,
  type SalesforceContact,
  type SalesforceObjectType,
} from '../../store/promoterApi';

type ImportStep = 'source' | 'upload' | 'hubspot-connect' | 'hubspot-contacts' | 'salesforce-connect' | 'mapping' | 'review' | 'complete';
type ImportSource = 'csv' | 'hubspot' | 'salesforce' | null;

type FieldMapping = {
  sourceField: string;
  mappedTo: string | null;
  sample: string;
  required: boolean;
};

type ParsedRow = Record<string, string>;

// Auto-mapping heuristic
const AUTO_MAP: Record<string, string> = {
  firstname: 'firstName',
  first_name: 'firstName',
  'first name': 'firstName',
  prenom: 'firstName',
  lastname: 'lastName',
  last_name: 'lastName',
  'last name': 'lastName',
  nom: 'lastName',
  email: 'email',
  'e-mail': 'email',
  emailaddress: 'email',
  email_address: 'email',
  phone: 'phone',
  telephone: 'phone',
  mobile: 'phone',
  tel: 'phone',
  company: 'company',
  organization: 'company',
  organisation: 'company',
  entreprise: 'company',
  linkedin: 'linkedin',
  linkedinurl: 'linkedin',
  linkedin_url: 'linkedin',
  companysize: 'companySize',
  company_size: 'companySize',
  'company size': 'companySize',
};

const AVAILABLE_FIELDS = [
  { value: 'firstName', labelKey: 'crmImport.availableFields.firstName', required: true },
  { value: 'lastName', labelKey: 'crmImport.availableFields.lastName', required: true },
  { value: 'email', labelKey: 'crmImport.availableFields.email', required: true },
  { value: 'phone', labelKey: 'crmImport.availableFields.phone', required: false },
  { value: 'company', labelKey: 'crmImport.availableFields.company', required: false },
  { value: 'linkedin', labelKey: 'crmImport.availableFields.linkedin', required: false },
  { value: 'companySize', labelKey: 'crmImport.availableFields.companySize', required: false },
];

const REQUIRED_FIELDS = AVAILABLE_FIELDS.filter(f => f.required).map(f => f.value);

const CRMImport: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState<ImportStep>('source');
  const [importSource, setImportSource] = useState<ImportSource>(null);
  const [file, setFile] = useState<File | null>(null);
  const [parsedHeaders, setParsedHeaders] = useState<string[]>([]);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [importResponse, setImportResponse] = useState<BatchImportResponse | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  // HubSpot state
  const [hubSpotContacts, setHubSpotContacts] = useState<HubSpotContact[]>([]);
  const [hubSpotSourceFields, setHubSpotSourceFields] = useState<string[]>([]);

  // Salesforce state
  const [salesforceContacts, setSalesforceContacts] = useState<SalesforceContact[]>([]);
  const [salesforceObjectType, setSalesforceObjectType] = useState<SalesforceObjectType>('contact');

  // API hooks
  const [batchImport, { isLoading: isImporting }] = useBatchImportPromotersMutation();
  const { data: hubSpotStatus } = useGetHubSpotStatusQuery(undefined, {
    skip: importSource !== 'hubspot',
  });
  const { data: hubSpotAuthUrl } = useGetHubSpotAuthUrlQuery(undefined, {
    skip: importSource !== 'hubspot',
  });
  const [fetchHubSpotContacts, { isFetching: isFetchingContacts }] = useLazyGetHubSpotContactsQuery();
  const { data: hubSpotProperties } = useGetHubSpotContactPropertiesQuery(undefined, {
    skip: importSource !== 'hubspot' || !hubSpotStatus?.connected,
  });
  const [disconnectHubSpot] = useDisconnectHubSpotMutation();
  const { data: salesforceStatus } = useGetSalesforceStatusQuery(undefined, {
    skip: importSource !== 'salesforce',
  });
  const { data: salesforceAuthUrl } = useGetSalesforceAuthUrlQuery(undefined, {
    skip: importSource !== 'salesforce' || salesforceStatus?.connected !== false,
  });
  const [fetchSalesforceContacts, { isFetching: isFetchingSalesforceContacts }] = useLazyGetSalesforceContactsQuery();
  const [disconnectSalesforce] = useDisconnectSalesforceMutation();

  // Check if redirected back from CRM OAuth
  const hubSpotConnected = searchParams.get('hubspot') === 'connected';
  const salesforceConnected = searchParams.get('salesforce') === 'connected';

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      parseCSVFile(selectedFile);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile && (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv'))) {
      parseCSVFile(droppedFile);
    }
  };

  const parseCSVFile = useCallback((csvFile: File) => {
    setParseError(null);
    setFile(csvFile);

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0 && results.data.length === 0) {
          setParseError(t('crmImport.upload.errors.parseFailed'));
          return;
        }

        const headers = results.meta.fields || [];
        const rows = results.data as ParsedRow[];

        if (headers.length === 0) {
          setParseError(t('crmImport.upload.errors.noHeaders'));
          return;
        }

        if (rows.length === 0) {
          setParseError(t('crmImport.upload.errors.noRows'));
          return;
        }

        if (rows.length > 200) {
          setParseError(t('crmImport.upload.errors.tooManyRows', { rows: rows.length }));
          return;
        }

        setParsedHeaders(headers);
        setParsedRows(rows);

        // Auto-map fields
        const mappings: FieldMapping[] = headers.map(header => {
          const normalized = header.toLowerCase().trim().replace(/[^a-z0-9_]/g, '');
          const autoMapped = AUTO_MAP[normalized] || AUTO_MAP[header.toLowerCase().trim()] || null;
          return {
            sourceField: header,
            mappedTo: autoMapped,
            sample: rows[0]?.[header] || '',
            required: false, // will be updated based on mapped field
          };
        });

        setFieldMappings(mappings);
      },
      error: () => {
        setParseError(t('crmImport.upload.errors.readFailed'));
      }
    });
  }, [t]);

  const handleFieldMapping = (sourceField: string, value: string | null) => {
    setFieldMappings(prev =>
      prev.map(field =>
        field.sourceField === sourceField ? { ...field, mappedTo: value } : field
      )
    );
  };

  const getMappedFieldsCount = () => fieldMappings.filter(f => f.mappedTo !== null).length;

  const getRequiredFieldsMapped = () => {
    const mappedValues = fieldMappings.filter(f => f.mappedTo !== null).map(f => f.mappedTo);
    return REQUIRED_FIELDS.filter(rf => mappedValues.includes(rf)).length;
  };

  const canProceedToReview = () => getRequiredFieldsMapped() === REQUIRED_FIELDS.length;

  // Build the import entries from parsed data + mappings
  const buildImportEntries = useCallback((): { entries: BatchImportPromoterEntry[]; validationErrors: string[] } => {
    const sourceRows =
      importSource === 'csv' ? parsedRows :
      importSource === 'salesforce' ? salesforceContacts.map(c => c.properties as unknown as ParsedRow) :
      hubSpotContacts.map(c => c.properties as unknown as ParsedRow);
    const entries: BatchImportPromoterEntry[] = [];
    const validationErrors: string[] = [];
    const seenEmails = new Set<string>();

    const getMapping = (targetField: string): string | null => {
      const mapping = fieldMappings.find(f => f.mappedTo === targetField);
      return mapping?.sourceField || null;
    };

    sourceRows.forEach((row, index) => {
      const firstNameField = getMapping('firstName');
      const lastNameField = getMapping('lastName');
      const emailField = getMapping('email');
      const phoneField = getMapping('phone');
      const companyField = getMapping('company');
      const linkedinField = getMapping('linkedin');
      const companySizeField = getMapping('companySize');

      const firstName = firstNameField ? (row[firstNameField] || '').trim() : '';
      const lastName = lastNameField ? (row[lastNameField] || '').trim() : '';
      const email = emailField ? (row[emailField] || '').trim().toLowerCase() : '';
      const phone = phoneField ? (row[phoneField] || '').trim() : undefined;
      const company = companyField ? (row[companyField] || '').trim() : undefined;
      const linkedin = linkedinField ? (row[linkedinField] || '').trim() : undefined;
      const companySize = companySizeField ? (row[companySizeField] || '').trim() : undefined;

      if (!email) {
        validationErrors.push(t('crmImport.review.validationRows.missingEmail', { row: index + 1 }));
        return;
      }

      if (!firstName) {
        validationErrors.push(t('crmImport.review.validationRows.missingFirstName', { row: index + 1, email }));
        return;
      }

      if (!lastName) {
        validationErrors.push(t('crmImport.review.validationRows.missingLastName', { row: index + 1, email }));
        return;
      }

      if (seenEmails.has(email)) {
        validationErrors.push(t('crmImport.review.validationRows.duplicateEmail', { row: index + 1, email }));
        return;
      }

      seenEmails.add(email);

      entries.push({
        first_name: firstName,
        last_name: lastName,
        email,
        phone: phone || undefined,
        company: company || undefined,
        linkedin: linkedin || undefined,
        company_size: companySize || undefined,
      });
    });

    return { entries, validationErrors };
  }, [parsedRows, hubSpotContacts, salesforceContacts, fieldMappings, importSource, t]);

  const previewData = useMemo(() => {
    const { entries, validationErrors } = buildImportEntries();
    return { entries, validationErrors };
  }, [buildImportEntries]);

  const handleImport = async () => {
    const { entries } = previewData;
    if (entries.length === 0) return;

    try {
      const response = await batchImport({
        promoters: entries,
        import_source: importSource as 'csv' | 'hubspot' | 'salesforce',
      }).unwrap();
      setImportResponse(response);
      setCurrentStep('complete');
    } catch (err: any) {
      setImportResponse(null);
      alert(err?.data?.message || t('crmImport.review.importFailed'));
    }
  };

  const downloadErrorReport = () => {
    if (!importResponse) return;
    const errorResults = importResponse.results.filter(r => r.status === 'error');
    if (errorResults.length === 0) return;

    const csv = Papa.unparse(
      errorResults.map(r => ({ email: r.email, status: r.status, error: r.error || '' }))
    );
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'import-error-report.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  // HubSpot handlers
  const handleHubSpotConnect = () => {
    if (hubSpotAuthUrl?.auth_url) {
      window.location.href = hubSpotAuthUrl.auth_url;
    }
  };

  const handleFetchHubSpotContacts = async () => {
    try {
      const allContacts: HubSpotContact[] = [];
      let after: string | undefined;
      let hasMore = true;

      while (hasMore) {
        const result = await fetchHubSpotContacts({ limit: 100, after }).unwrap();
        allContacts.push(...result.contacts);
        hasMore = result.has_more;
        after = result.after;
        if (allContacts.length >= 200) break; // respect batch limit
      }

      setHubSpotContacts(allContacts.slice(0, 200));

      // Build field mappings from HubSpot properties
      const sampleContact = allContacts[0];
      if (sampleContact) {
        const propKeys = Object.keys(sampleContact.properties);
        setHubSpotSourceFields(propKeys);

        const mappings: FieldMapping[] = propKeys.map(key => {
          const normalized = key.toLowerCase().trim().replace(/[^a-z0-9_]/g, '');
          const autoMapped = AUTO_MAP[normalized] || AUTO_MAP[key.toLowerCase().trim()] || null;
          return {
            sourceField: key,
            mappedTo: autoMapped,
            sample: sampleContact.properties[key] || '',
            required: false,
          };
        });

        setFieldMappings(mappings);
      }

      setCurrentStep('mapping');
    } catch {
      alert(t('crmImport.hubspotConnect.fetchError'));
    }
  };

  // Salesforce handlers
  const handleSalesforceConnect = () => {
    if (salesforceAuthUrl?.auth_url) {
      window.location.href = salesforceAuthUrl.auth_url;
    }
  };

  const handleFetchSalesforceContacts = async () => {
    try {
      const allContacts: SalesforceContact[] = [];
      let after: string | undefined;
      let hasMore = true;

      while (hasMore) {
        const result = await fetchSalesforceContacts({ limit: 100, after, objectType: salesforceObjectType }).unwrap();
        allContacts.push(...result.contacts);
        hasMore = result.has_more;
        after = result.after;
        if (allContacts.length >= 200) break; // respect batch limit
      }

      setSalesforceContacts(allContacts.slice(0, 200));

      const sampleContact = allContacts[0];
      if (sampleContact) {
        const propKeys = Object.keys(sampleContact.properties);
        const mappings: FieldMapping[] = propKeys.map(key => {
          const normalized = key.toLowerCase().trim().replace(/[^a-z0-9_]/g, '');
          const autoMapped = AUTO_MAP[normalized] || AUTO_MAP[key.toLowerCase().trim()] || null;
          return {
            sourceField: key,
            mappedTo: autoMapped,
            sample: sampleContact.properties[key] || '',
            required: false,
          };
        });

        setFieldMappings(mappings);
      }

      setCurrentStep('mapping');
    } catch {
      alert(t('crmImport.salesforceConnect.fetchError'));
    }
  };

  // Step indicator
  const getSteps = (): { key: ImportStep; label: string }[] => {
    if (importSource === 'salesforce') {
      return [
        { key: 'source', label: t('crmImport.steps.source') },
        { key: 'salesforce-connect', label: t('crmImport.steps.connect') },
        { key: 'mapping', label: t('crmImport.steps.mapping') },
        { key: 'review', label: t('crmImport.steps.review') },
        { key: 'complete', label: t('crmImport.steps.complete') },
      ];
    }
    if (importSource === 'hubspot') {
      return [
        { key: 'source', label: t('crmImport.steps.source') },
        { key: 'hubspot-connect', label: t('crmImport.steps.connect') },
        { key: 'mapping', label: t('crmImport.steps.mapping') },
        { key: 'review', label: t('crmImport.steps.review') },
        { key: 'complete', label: t('crmImport.steps.complete') },
      ];
    }
    return [
      { key: 'source', label: t('crmImport.steps.source') },
      { key: 'upload', label: t('crmImport.steps.upload') },
      { key: 'mapping', label: t('crmImport.steps.mapping') },
      { key: 'review', label: t('crmImport.steps.review') },
      { key: 'complete', label: t('crmImport.steps.complete') },
    ];
  };

  const renderStepIndicator = () => {
    const steps = getSteps();
    const currentIndex = steps.findIndex(s => s.key === currentStep);

    return (
      <div className="flex items-center justify-center space-x-4 mb-8">
        {steps.map((step, index) => (
          <React.Fragment key={step.key}>
            {index > 0 && <ArrowRight className="w-5 h-5 text-gray-400" />}
            <div className={`flex items-center space-x-2 ${index <= currentIndex ? 'text-[#4339A0]' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                index <= currentIndex ? 'bg-[#4339A0] text-white' : 'bg-gray-200'
              }`}>
                {index < currentIndex ? <Check className="w-5 h-5" /> : index + 1}
              </div>
              <span className="font-medium">{step.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderSourceStep = () => (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('crmImport.source.title')}</h2>
        <p className="text-gray-600">{t('crmImport.source.description')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <button
          onClick={() => {
            setImportSource('csv');
            setCurrentStep('upload');
          }}
          className="group relative bg-blue-50 border-2 border-blue-200 rounded-xl p-8 hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-left"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-lg font-bold text-blue-900 mb-2">{t('crmImport.source.csvTitle')}</h3>
          <p className="text-gray-600 text-sm mb-4">{t('crmImport.source.csvDesc')}</p>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-gradient-to-br from-blue-500 to-blue-600" />
            <span className="text-xs text-gray-500 font-medium">{t('crmImport.source.clickToContinue')}</span>
          </div>
        </button>

        <button
          onClick={() => {
            setImportSource('hubspot');
            setCurrentStep('hubspot-connect');
          }}
          className="group relative bg-[#FFF0ED] border-2 border-[#FF7A59]/30 rounded-xl p-8 hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-left"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-[#FF7A59] to-[#FF5C35] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
            <Cloud className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-lg font-bold text-[#FF5C35] mb-2">{t('crmImport.source.hubspotTitle')}</h3>
          <p className="text-gray-600 text-sm mb-4">{t('crmImport.source.hubspotDesc')}</p>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-gradient-to-br from-[#FF7A59] to-[#FF5C35]" />
            <span className="text-xs text-gray-500 font-medium">{t('crmImport.source.clickToContinue')}</span>
          </div>
        </button>

        <button
          onClick={() => {
            setImportSource('salesforce');
            setCurrentStep('salesforce-connect');
          }}
          className="group relative bg-[#EAF6FC] border-2 border-[#00A1E0]/30 rounded-xl p-8 hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-left"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-[#00A1E0] to-[#0070A8] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
            <Cloud className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-lg font-bold text-[#0070A8] mb-2">{t('crmImport.source.salesforceTitle')}</h3>
          <p className="text-gray-600 text-sm mb-4">{t('crmImport.source.salesforceDesc')}</p>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-gradient-to-br from-[#00A1E0] to-[#0070A8]" />
            <span className="text-xs text-gray-500 font-medium">{t('crmImport.source.clickToContinue')}</span>
          </div>
        </button>
      </div>

      <div className="mt-8 bg-gray-50 rounded-xl p-6 border border-gray-200">
        <div className="flex items-start space-x-3">
          <Upload className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">{t('crmImport.source.tipsTitle')}</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>&#8226; <strong>{t('crmImport.source.tipCsv')}</strong> {t('crmImport.source.tipCsvText')}</li>
              <li>&#8226; <strong>{t('crmImport.source.tipHubSpot')}</strong> {t('crmImport.source.tipHubSpotText')}</li>
              <li>&#8226; {t('crmImport.source.tipMax')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderUploadStep = () => (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('crmImport.upload.title')}</h2>
        <p className="text-gray-600">{t('crmImport.upload.description')}</p>
      </div>

      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-gray-300 rounded-2xl p-12 text-center bg-gray-50 hover:border-[#4339A0] hover:bg-[#f5f3ff] transition-all"
      >
        <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {file ? file.name : t('crmImport.upload.dropHere')}
        </h3>
        <p className="text-gray-600 mb-6">{t('crmImport.upload.orBrowse')}</p>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload"
        />
        <label
          htmlFor="file-upload"
          className="inline-flex items-center space-x-2 px-6 py-3 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors cursor-pointer"
        >
          <FileText className="w-5 h-5" />
          <span>{t('crmImport.upload.chooseFile')}</span>
        </label>
      </div>

      {parseError && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <p className="font-medium text-red-900">{parseError}</p>
          </div>
        </div>
      )}

      {file && !parseError && parsedRows.length > 0 && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-medium text-green-900">{file.name}</p>
                <p className="text-sm text-green-700">
                  {t('crmImport.upload.recordsFound', { records: parsedRows.length, columns: parsedHeaders.length })}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setFile(null);
                setParsedHeaders([]);
                setParsedRows([]);
                setFieldMappings([]);
                setParseError(null);
              }}
              className="p-2 hover:bg-green-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-green-700" />
            </button>
          </div>
        </div>
      )}

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <HelpCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-2">{t('crmImport.upload.formatTitle')}</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>{t('crmImport.upload.formatHeader')}</li>
              <li>{t('crmImport.upload.formatRequired')}</li>
              <li>{t('crmImport.upload.formatEncoding')}</li>
              <li>{t('crmImport.upload.formatMax')}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-between">
        <button
          onClick={() => {
            setFile(null);
            setParsedHeaders([]);
            setParsedRows([]);
            setFieldMappings([]);
            setParseError(null);
            setImportSource(null);
            setCurrentStep('source');
          }}
          className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('crmImport.upload.back')}</span>
        </button>
        {file && !parseError && parsedRows.length > 0 && (
          <button
            onClick={() => setCurrentStep('mapping')}
            className="flex items-center space-x-2 px-6 py-3 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors"
          >
            <span>{t('crmImport.upload.continueToMapping')}</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );

  const renderHubSpotConnectStep = () => {
    const isConnected = hubSpotStatus?.connected || hubSpotConnected;

    return (
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('crmImport.hubspotConnect.title')}</h2>
          <p className="text-gray-600">{t('crmImport.hubspotConnect.description')}</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-[#FF7A59] to-[#FF5C35] rounded-xl flex items-center justify-center mx-auto mb-6">
            <Cloud className="w-10 h-10 text-white" />
          </div>

          {isConnected ? (
            <>
              <div className="flex items-center justify-center space-x-2 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <h3 className="text-xl font-bold text-green-700">{t('crmImport.hubspotConnect.connected')}</h3>
              </div>
              {hubSpotStatus?.hub_id && (
                <p className="text-sm text-gray-500 mb-6">{t('crmImport.hubspotConnect.hubId', { id: hubSpotStatus.hub_id })}</p>
              )}
              <div className="flex items-center justify-center space-x-4">
                <button
                  onClick={handleFetchHubSpotContacts}
                  disabled={isFetchingContacts}
                  className="flex items-center space-x-2 px-6 py-3 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors disabled:opacity-50"
                >
                  {isFetchingContacts ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{t('crmImport.hubspotConnect.fetching')}</span>
                    </>
                  ) : (
                    <>
                      <Database className="w-5 h-5" />
                      <span>{t('crmImport.hubspotConnect.fetchContinue')}</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => disconnectHubSpot()}
                  className="flex items-center space-x-2 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                  <span>{t('crmImport.hubspotConnect.disconnect')}</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('crmImport.hubspotConnect.connectHeading')}</h3>
              <p className="text-gray-600 mb-6">
                {t('crmImport.hubspotConnect.connectBody')}
              </p>
              <button
                onClick={handleHubSpotConnect}
                disabled={!hubSpotAuthUrl}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-[#FF7A59] text-white rounded-lg hover:bg-[#FF5C35] transition-colors disabled:opacity-50"
              >
                <Cloud className="w-5 h-5" />
                <span>{t('crmImport.hubspotConnect.connectButton')}</span>
              </button>
            </>
          )}
        </div>

        <div className="mt-8 flex justify-start">
          <button
            onClick={() => {
              setImportSource(null);
              setCurrentStep('source');
            }}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('crmImport.hubspotConnect.back')}</span>
          </button>
        </div>
      </div>
    );
  };

  const renderSalesforceConnectStep = () => {
    const isConnected = salesforceStatus?.connected || salesforceConnected;

    return (
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('crmImport.salesforceConnect.title')}</h2>
          <p className="text-gray-600">{t('crmImport.salesforceConnect.description')}</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-[#00A1E0] to-[#0070A8] rounded-xl flex items-center justify-center mx-auto mb-6">
            <Cloud className="w-10 h-10 text-white" />
          </div>

          {isConnected ? (
            <>
              <div className="flex items-center justify-center space-x-2 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <h3 className="text-xl font-bold text-green-700">{t('crmImport.salesforceConnect.connected')}</h3>
              </div>
              {salesforceStatus?.org_id && (
                <p className="text-sm text-gray-500 mb-6">{t('crmImport.salesforceConnect.orgId', { id: salesforceStatus.org_id })}</p>
              )}

              <div className="flex items-center justify-center space-x-2 mb-6">
                <span className="text-sm text-gray-600">{t('crmImport.salesforceConnect.objectTypeLabel')}</span>
                <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                  {(['contact', 'lead'] as SalesforceObjectType[]).map((type) => (
                    <button
                      key={type}
                      onClick={() => setSalesforceObjectType(type)}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        salesforceObjectType === type ? 'bg-[#4339A0] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {t(`crmImport.salesforceConnect.objectType.${type}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-center space-x-4">
                <button
                  onClick={handleFetchSalesforceContacts}
                  disabled={isFetchingSalesforceContacts}
                  className="flex items-center space-x-2 px-6 py-3 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors disabled:opacity-50"
                >
                  {isFetchingSalesforceContacts ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{t('crmImport.salesforceConnect.fetching')}</span>
                    </>
                  ) : (
                    <>
                      <Database className="w-5 h-5" />
                      <span>{t('crmImport.salesforceConnect.fetchContinue')}</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => disconnectSalesforce()}
                  className="flex items-center space-x-2 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                  <span>{t('crmImport.salesforceConnect.disconnect')}</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('crmImport.salesforceConnect.connectHeading')}</h3>
              <p className="text-gray-600 mb-6">
                {t('crmImport.salesforceConnect.connectBody')}
              </p>
              <button
                onClick={handleSalesforceConnect}
                disabled={!salesforceAuthUrl}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-[#00A1E0] text-white rounded-lg hover:bg-[#0070A8] transition-colors disabled:opacity-50"
              >
                <Cloud className="w-5 h-5" />
                <span>{t('crmImport.salesforceConnect.connectButton')}</span>
              </button>
            </>
          )}
        </div>

        <div className="mt-8 flex justify-start">
          <button
            onClick={() => {
              setImportSource(null);
              setCurrentStep('source');
            }}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('crmImport.salesforceConnect.back')}</span>
          </button>
        </div>
      </div>
    );
  };

  const renderMappingStep = () => (
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('crmImport.mapping.title')}</h2>
        <p className="text-gray-600">{t('crmImport.mapping.description', { source: importSource === 'csv' ? t('crmImport.mapping.sourceCsv') : importSource === 'salesforce' ? t('crmImport.mapping.sourceSalesforce') : t('crmImport.mapping.sourceHubSpot') })}</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('crmImport.mapping.progress')}</h3>
          <div className="text-sm">
            <span className="text-gray-600">{t('crmImport.mapping.mappedCount', { mapped: getMappedFieldsCount(), total: fieldMappings.length })}</span>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-[#4339A0] h-2 rounded-full transition-all"
            style={{ width: `${fieldMappings.length > 0 ? (getMappedFieldsCount() / fieldMappings.length) * 100 : 0}%` }}
          />
        </div>
        {!canProceedToReview() && (
          <div className="mt-4 flex items-center space-x-2 text-orange-700 bg-orange-50 p-3 rounded-lg">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">
              {t('crmImport.mapping.requiredHint', { mapped: getRequiredFieldsMapped(), total: REQUIRED_FIELDS.length })}
            </p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">{t('crmImport.mapping.columns.source')}</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">{t('crmImport.mapping.columns.sample')}</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">{t('crmImport.mapping.columns.mapTo')}</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">{t('crmImport.mapping.columns.status')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {fieldMappings.map((field) => {
              const isRequiredTarget = field.mappedTo ? REQUIRED_FIELDS.includes(field.mappedTo) : false;
              return (
                <tr key={field.sourceField} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span className="font-medium text-gray-900">{field.sourceField}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600 italic">{field.sample || '—'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={field.mappedTo || ''}
                      onChange={(e) => handleFieldMapping(field.sourceField, e.target.value || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent text-sm"
                    >
                      <option value="">{t('crmImport.mapping.skip')}</option>
                      {AVAILABLE_FIELDS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {t(option.labelKey)} {option.required ? t('crmImport.mapping.required') : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    {field.mappedTo ? (
                      <div className="flex items-center space-x-2 text-green-600">
                        <Check className="w-5 h-5" />
                        <span className="text-sm font-medium">{t('crmImport.mapping.mapped')}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">{t('crmImport.mapping.notMapped')}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-8 flex justify-between">
        <button
          onClick={() => {
            if (importSource === 'hubspot') {
              setCurrentStep('hubspot-connect');
            } else if (importSource === 'salesforce') {
              setCurrentStep('salesforce-connect');
            } else {
              setCurrentStep('upload');
            }
          }}
          className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('crmImport.mapping.back')}</span>
        </button>
        <button
          onClick={() => setCurrentStep('review')}
          disabled={!canProceedToReview()}
          className="flex items-center space-x-2 px-6 py-3 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span>{t('crmImport.mapping.continueToReview')}</span>
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  const renderReviewStep = () => {
    const { entries, validationErrors } = previewData;
    const totalRecords =
      importSource === 'csv' ? parsedRows.length :
      importSource === 'salesforce' ? salesforceContacts.length :
      hubSpotContacts.length;

    return (
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('crmImport.review.title')}</h2>
          <p className="text-gray-600">{t('crmImport.review.description')}</p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-3xl font-bold text-gray-900 mb-1">{totalRecords}</div>
            <div className="text-sm text-gray-600">{t('crmImport.review.totalRecords')}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-3xl font-bold text-green-600 mb-1">{entries.length}</div>
            <div className="text-sm text-gray-600">{t('crmImport.review.validRecords')}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-3xl font-bold text-orange-600 mb-1">{validationErrors.length}</div>
            <div className="text-sm text-gray-600">{t('crmImport.review.validationIssues')}</div>
          </div>
        </div>

        {/* Field Mappings Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('crmImport.review.fieldMappings')}</h3>
          <div className="space-y-3">
            {fieldMappings.filter(f => f.mappedTo).map((field) => {
              const targetField = AVAILABLE_FIELDS.find(f => f.value === field.mappedTo);
              return (
                <div key={field.sourceField} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Settings className="w-5 h-5 text-gray-400" />
                    <span className="font-medium text-gray-900">{field.sourceField}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-[#4339A0]">
                      {targetField ? t(targetField.labelKey) : ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Preview Table */}
        {entries.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{t('crmImport.review.previewTitle')}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('crmImport.previewColumns.firstName')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('crmImport.previewColumns.lastName')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('crmImport.previewColumns.email')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('crmImport.previewColumns.company')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('crmImport.previewColumns.phone')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {entries.slice(0, 5).map((entry, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{entry.first_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{entry.last_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{entry.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{entry.company || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{entry.phone || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-orange-600" />
                <span className="font-medium text-orange-900">{t('crmImport.review.issuesFound', { count: validationErrors.length })}</span>
              </div>
              <button
                onClick={() => setShowErrors(!showErrors)}
                className="text-sm text-orange-700 hover:text-orange-900 flex items-center space-x-1"
              >
                <span>{showErrors ? t('crmImport.review.hideDetails') : t('crmImport.review.showDetails')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showErrors ? 'rotate-180' : ''}`} />
              </button>
            </div>
            {showErrors && (
              <ul className="mt-2 space-y-1 text-sm text-orange-800">
                {validationErrors.slice(0, 20).map((error, idx) => (
                  <li key={idx}>&#8226; {error}</li>
                ))}
                {validationErrors.length > 20 && (
                  <li className="font-medium">{t('crmImport.review.moreErrors', { count: validationErrors.length - 20 })}</li>
                )}
              </ul>
            )}
          </div>
        )}

        <div className="flex justify-between">
          <button
            onClick={() => setCurrentStep('mapping')}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('crmImport.review.backToMapping')}</span>
          </button>
          <button
            onClick={handleImport}
            disabled={isImporting || entries.length === 0}
            className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isImporting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{t('crmImport.review.saving', { count: entries.length })}</span>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                <span>{t('crmImport.review.saveContacts', { count: entries.length })}</span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderCompleteStep = () => {
    if (!importResponse) return null;

    const errorResults = importResponse.results.filter(r => r.status === 'error');

    return (
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('crmImport.complete.title')}</h2>
          <p className="text-gray-600">
            {t('crmImport.complete.description', { count: importResponse?.success_count ?? 0 })}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">{t('crmImport.complete.summaryTitle')}</h3>

          <div className="grid grid-cols-3 gap-6 mb-8">
            <div className="text-center p-6 bg-green-50 rounded-xl">
              <div className="text-4xl font-bold text-green-600 mb-2">{importResponse.success_count}</div>
              <div className="text-sm text-green-700 font-medium">{t('crmImport.complete.saved')}</div>
            </div>
            <div className="text-center p-6 bg-orange-50 rounded-xl">
              <div className="text-4xl font-bold text-orange-600 mb-2">{importResponse.duplicate_count}</div>
              <div className="text-sm text-orange-700 font-medium">{t('crmImport.complete.duplicates')}</div>
            </div>
            <div className="text-center p-6 bg-red-50 rounded-xl">
              <div className="text-4xl font-bold text-red-600 mb-2">{importResponse.error_count}</div>
              <div className="text-sm text-red-700 font-medium">{t('crmImport.complete.failed')}</div>
            </div>
          </div>

          {/* Error Details */}
          {errorResults.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-red-900">{t('crmImport.complete.failedRecords')}</h4>
                <button
                  onClick={downloadErrorReport}
                  className="flex items-center space-x-2 text-sm text-red-700 hover:text-red-900"
                >
                  <Download className="w-4 h-4" />
                  <span>{t('crmImport.complete.downloadErrorReport')}</span>
                </button>
              </div>
              <div className="bg-red-50 rounded-lg border border-red-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-red-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-red-800">{t('crmImport.previewColumns.email')}</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-red-800">{t('crmImport.previewColumns.error')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-200">
                    {errorResults.slice(0, 10).map((result, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2 text-sm text-red-900">{result.email}</td>
                        <td className="px-4 py-2 text-sm text-red-700">{result.error || t('crmImport.complete.unknownError')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {errorResults.length > 10 && (
                  <div className="px-4 py-2 text-sm text-red-700 border-t border-red-200">
                    {t('crmImport.complete.moreFailed', { count: errorResults.length - 10 })}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={() => navigate('/promoters')}
              className="flex items-center space-x-2 px-6 py-3 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors"
            >
              <span>{t('crmImport.complete.viewPromoters')}</span>
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                setFile(null);
                setParsedHeaders([]);
                setParsedRows([]);
                setFieldMappings([]);
                setImportSource(null);
                setImportResponse(null);
                setHubSpotContacts([]);
                setSalesforceContacts([]);
                setCurrentStep('source');
              }}
              className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Upload className="w-5 h-5" />
              <span>{t('crmImport.complete.saveMore')}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        <button
          onClick={() => navigate('/promoters')}
          className="mb-6 flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('crmImport.backToPromoters')}</span>
        </button>

        {renderStepIndicator()}

        <div className="mt-8">
          {currentStep === 'source' && renderSourceStep()}
          {currentStep === 'upload' && renderUploadStep()}
          {currentStep === 'hubspot-connect' && renderHubSpotConnectStep()}
          {currentStep === 'salesforce-connect' && renderSalesforceConnectStep()}
          {currentStep === 'mapping' && renderMappingStep()}
          {currentStep === 'review' && renderReviewStep()}
          {currentStep === 'complete' && renderCompleteStep()}
        </div>
      </div>
    </div>
  );
};

export default CRMImport;
